import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { TooltipProvider } from '@/components/ui/tooltip'
import { Minus, Plus, Trash2, Ban, Send, Users } from 'lucide-react'
import type { KOTItem } from './KOTServerInterface'
import { isKotUnsentStatus } from './kotConstants'
import { VOIDABLE_STATUSES } from '@/components/counter/rail/SentItemsSection'
import { useCurrency } from '@/contexts/CurrencyContext'
import { ItemNotesPopover, OrderNotePopover } from '@/components/pos/NotesPopovers'

interface KOTSidebarProps {
  items: KOTItem[]
  tableName: string
  guestCount: number
  orderId: string | null
  orderNotes: string
  onOrderNotesChange: (notes: string) => void
  onUpdateQty: (id: string, qty: number) => void
  onSetItemNotes: (id: string, note: string) => void
  onRemoveDraft: (id: string) => void
  onRequestVoid: (id: string, name: string, qty: number, price: number) => void
  /** Void every sent line under one reason + manager PIN (whole-order mode). */
  onRequestVoidOrder?: () => void
  onFireKOT: () => void
  isFireLoading: boolean
  /** Human-readable chosen options for a line ("Large", "Beef"). */
  labelsForLine?: (item: KOTItem) => string[]
  /** Reopen the option sheet for a staged line. */
  onEditOptions?: (id: string) => void
}

export function KOTSidebar({
  items,
  tableName,
  guestCount,
  orderId,
  orderNotes,
  onOrderNotesChange,
  onUpdateQty,
  onSetItemNotes,
  onRemoveDraft,
  onRequestVoid,
  onRequestVoidOrder,
  onFireKOT,
  isFireLoading,
  labelsForLine,
  onEditOptions,
}: KOTSidebarProps) {
  const { formatCurrency } = useCurrency()

  const draftItems = items.filter((i) => isKotUnsentStatus(i.status))
  // Same voidable set as the counter (F-CTRL-N41): a 'served' line is still on
  // the bill and still voidable, and used to drop out of this list entirely.
  const sentItems = items.filter((i) => VOIDABLE_STATUSES.has(i.status))
  const voidedItems = items.filter((i) => i.status === 'voided')

  const activeItems = items.filter((i) => i.status !== 'voided')
  // Menu prices only (F-FE-N126). This used to add a hard-coded 10% "Sales
  // Tax (est.)" and print a bold Total — a rate no tenant configured, wrong on
  // every 0%-tax tenant, and a figure the floor cannot know anyway: the real
  // total depends on the tender and the tenant's service-charge settings. The
  // bill is the counter's; the action rail's Print bill shows the server's.
  const subtotal = activeItems.reduce((sum, i) => sum + i.unit_price * i.quantity, 0)

  const itemsWithNotes = draftItems.filter((i) => (i.special_instructions ?? '').trim().length > 0).length
  const orderNotePresent = orderNotes.trim().length > 0
  const totalNoteCount = itemsWithNotes + (orderNotePresent ? 1 : 0)

  return (
    <TooltipProvider delayDuration={250}>
      <div className="flex h-full min-h-0 w-full min-w-0 flex-col border-l border-border bg-card lg:w-80">
        <div className="border-b border-border bg-muted/40 p-4">
          <div className="flex items-center justify-between gap-2">
            <div className="min-w-0">
              <div className="truncate text-lg font-semibold tracking-tight text-foreground">
                Table {tableName}
              </div>
              <div className="mt-0.5 flex items-center gap-1.5">
                <Users className="h-3 w-3 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">
                  {guestCount} guest{guestCount !== 1 ? 's' : ''}
                </span>
              </div>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              {draftItems.length > 0 && (
                <OrderNotePopover
                  value={orderNotes}
                  onChange={onOrderNotesChange}
                  totalNoteCount={totalNoteCount}
                />
              )}
              {orderId && (
                <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500/10 px-2 py-0.5 text-[11px] font-medium text-emerald-600 dark:text-emerald-400">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                  Active
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          {items.length === 0 && (
            <div className="px-4 py-12 text-center text-sm text-muted-foreground">
              Tap items from the menu to add them
            </div>
          )}

          {draftItems.length > 0 && (
            <div>
              <div className="border-b border-border bg-amber-500/10 px-4 py-2">
                <span className="text-[11px] font-semibold uppercase tracking-wide text-amber-600 dark:text-amber-400">
                  Not sent ({draftItems.length})
                </span>
              </div>
              {draftItems.map((item) => (
                <div
                  key={item.id}
                  className="border-b border-l-4 border-border border-l-amber-400 px-4 py-3"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-medium text-foreground">
                        {item.product_name}
                      </div>
                      {/* The chosen size / protein. Shown on the ticket because a
                          waiter reading back an order to a guest needs it, and
                          because tapping it is how a wrong pick gets corrected. */}
                      {labelsForLine && onEditOptions && labelsForLine(item).length > 0 && (
                        <button
                          type="button"
                          onClick={() => onEditOptions(item.id)}
                          className="mt-0.5 text-left text-xs font-medium text-sky-700 underline decoration-dotted underline-offset-2 dark:text-sky-300"
                        >
                          {labelsForLine(item).join(' · ')}
                        </button>
                      )}
                      <div className="text-sm text-muted-foreground">
                        {formatCurrency(item.unit_price)}
                      </div>
                      {item.special_instructions?.trim() && (
                        <div className="mt-0.5 text-xs italic text-amber-700 dark:text-amber-400">
                          {item.special_instructions.trim()}
                        </div>
                      )}
                    </div>
                    <div className="text-sm font-semibold tabular-nums text-foreground">
                      {formatCurrency(item.unit_price * item.quantity)}
                    </div>
                  </div>
                  <div className="mt-2 flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => onUpdateQty(item.id, item.quantity - 1)}
                      className="flex h-7 w-7 items-center justify-center rounded-md border border-border hover:bg-muted"
                    >
                      <Minus className="h-3 w-3" />
                    </button>
                    <span className="w-6 text-center text-sm font-bold tabular-nums">
                      {item.quantity}
                    </span>
                    <button
                      type="button"
                      onClick={() => onUpdateQty(item.id, item.quantity + 1)}
                      className="flex h-7 w-7 items-center justify-center rounded-md border border-border hover:bg-muted"
                    >
                      <Plus className="h-3 w-3" />
                    </button>
                    <ItemNotesPopover
                      productName={item.product_name}
                      value={item.special_instructions ?? ''}
                      onChange={(note) => onSetItemNotes(item.id, note)}
                    />
                    <div className="flex-1" />
                    <button
                      type="button"
                      onClick={() => onRemoveDraft(item.id)}
                      className="flex h-7 w-7 items-center justify-center rounded-md text-red-500 hover:bg-red-500/10"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {sentItems.length > 0 && (
            <div>
              <div className="flex items-center justify-between gap-2 border-b border-border bg-emerald-500/10 px-4 py-2">
                <span className="text-[11px] font-semibold uppercase tracking-wide text-emerald-600 dark:text-emerald-400">
                  Sent ({sentItems.length})
                </span>
                {onRequestVoidOrder && orderId && (
                  <button
                    type="button"
                    onClick={onRequestVoidOrder}
                    title="Void every sent item on this table — needs a manager PIN"
                    className="inline-flex h-8 items-center gap-1 rounded-md border border-red-500/40 bg-card px-2 text-xs font-semibold text-red-600 transition-colors hover:bg-red-500/10 dark:text-red-400"
                  >
                    <Ban className="h-3.5 w-3.5" aria-hidden />
                    Void order
                  </button>
                )}
              </div>
              <p className="border-b border-border px-4 py-1.5 text-[11px] leading-snug text-muted-foreground">
                Tap Void to take an item off the bill — a manager PIN is needed.
              </p>
              {sentItems.map((item) => (
                <div
                  key={item.id}
                  className="border-b border-l-4 border-border border-l-emerald-400 px-4 py-3"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 text-sm font-medium text-foreground">
                        <span className="truncate">{item.product_name}</span>
                        <span className="shrink-0 rounded-full bg-emerald-500/10 px-1.5 py-0.5 text-[10px] font-medium capitalize text-emerald-600 dark:text-emerald-400">
                          {item.status}
                        </span>
                      </div>
                      <div className="text-sm text-muted-foreground">
                        x{item.quantity} @ {formatCurrency(item.unit_price)}
                      </div>
                      {item.special_instructions?.trim() && (
                        <div className="mt-0.5 text-xs italic text-muted-foreground">
                          {item.special_instructions.trim()}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold tabular-nums">
                        {formatCurrency(item.unit_price * item.quantity)}
                      </span>
                      <button
                        type="button"
                        onClick={() =>
                          onRequestVoid(
                            item.id,
                            item.product_name,
                            item.quantity,
                            item.unit_price,
                          )
                        }
                        className="inline-flex h-8 items-center gap-1 rounded-md border border-red-500/40 bg-card px-2 text-xs font-semibold text-red-600 transition-colors hover:bg-red-500/10 dark:text-red-400"
                        title="Void item (requires manager or admin PIN)"
                        aria-label={`Void ${item.product_name}`}
                      >
                        <Ban className="h-3.5 w-3.5" aria-hidden />
                        Void
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {voidedItems.length > 0 && (
            <div>
              <div className="border-b border-border bg-red-500/10 px-4 py-2">
                <span className="text-[11px] font-semibold uppercase tracking-wide text-red-600 dark:text-red-400">
                  Voided ({voidedItems.length})
                </span>
              </div>
              {voidedItems.map((item) => (
                <div
                  key={item.id}
                  className="border-b border-l-4 border-border border-l-red-400 px-4 py-3 opacity-60"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-medium text-muted-foreground line-through">
                        {item.product_name}
                      </div>
                      <div className="text-sm text-muted-foreground line-through">
                        x{item.quantity} @ {formatCurrency(item.unit_price)}
                      </div>
                    </div>
                    <Badge variant="destructive" className="text-xs">
                      VOIDED
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="border-t border-border bg-muted/40 p-4">
          {orderNotes.trim() && (
            <div className="mb-2 rounded-md border border-amber-500/30 bg-amber-500/10 px-2.5 py-1.5 text-xs text-amber-800 dark:text-amber-300">
              <span className="font-semibold">Order note: </span>
              {orderNotes.trim()}
            </div>
          )}
          <div className="space-y-1 text-sm">
            <div className="flex justify-between text-base font-semibold text-foreground">
              <span>Items subtotal ({activeItems.length} item{activeItems.length === 1 ? '' : 's'})</span>
              <span className="tabular-nums">{formatCurrency(subtotal)}</span>
            </div>
            <p className="text-xs text-muted-foreground">
              Tax and any service charge are added at the counter.
            </p>
          </div>
          <Button
            className="mt-3 h-12 w-full bg-orange-500 font-bold text-white hover:bg-orange-600"
            disabled={draftItems.length === 0 || isFireLoading}
            onClick={() => onFireKOT()}
          >
            <Send className="mr-2 h-4 w-4" />
            {isFireLoading
              ? 'Sending...'
              : `Fire KOT (${draftItems.length} item${draftItems.length !== 1 ? 's' : ''})`}
          </Button>
        </div>
      </div>
    </TooltipProvider>
  )
}
